import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface AddressTypes {
  city?: string;
  country?: string;
  zipCode?: string;
  barangay?: string;
  province?: string;
  subStreet?: string;
  mainStreet?: string;
  street?: string;
  buildingNumber?: string;
}

export const formatAddress = (address: AddressTypes): string => {
  const {
    city,
    country,
    zipCode,
    barangay,
    province,
    subStreet,
    street,
    buildingNumber,
  } = address;

  return [
    buildingNumber,
    subStreet,
    street,
    barangay,
    city,
    province,
    zipCode,
    country,
  ]
    .filter(Boolean)
    .join(', ');
};

export const formatInitialAddress = (address: AddressTypes): string => {
  const { city, barangay, subStreet, street, buildingNumber } = address;

  return [buildingNumber, subStreet, street, barangay, city]
    .filter(Boolean)
    .join(', ');
};
// ARRAY
export interface ContactTypes {
  contacts: ContactDetails[];
}

export interface ContactDetails {
  contact: { isPrimary: boolean; number: number; type: string };
}

export interface PhoneTypes {
  phones: PhoneDetails[];
}

export interface PhoneDetails {
  phone: { isPrimary: boolean; number: number; type: string };
}

export interface EmailTypes {
  emails: EmailDetails[];
}

export interface EmailDetails {
  email: { isPrimary: boolean; address: string };
}

export interface ModuleTypes {
  Module: {
    id: number;
    Code: string;
    Description: string;
    isActive: boolean;
    initialSetupDone: number;
  };
}
// END

export interface OwnerTypes {
  tin: string;
  lastName: string;
  firstName: string;
  middleName: string;
  designation: string;
}

export interface PropertyTypes {
  tin: string;
  rdoCode: string;
  branchCode: string;
  classification: string;
  lineOfBusiness: string;
}

export interface UserDataTypes {
  email: string;
  Modules: Array<ModuleTypes>;
  contact: string;
  fullName: string;
  position?: string;
  userRole: string;
  deparment?: string;
  shortName: string;
  accountLevel: string;
}

export interface ImagesTypes {
  logo: any;
  banner: any;
  reportLogo: any;
}

export interface TaxInfoTypes {
  owner: OwnerTypes;
  property: PropertyTypes;
  vatType?: string;
  scRatePercentage?: string;
  localTaxPercentage?: string;
}

export interface additionalDataTypes {
  bkCurrency: string;
  localTaxPercentage: string;
  propertyID: string;
  scRatePercentage: string;
  taxNumber: string;
  vatType: string;
}

export interface LicenseTypes {
  id: string;
  maxUser: string;
  description: string;
  expirationDate: string;
  expirationDays: number;
  maxConcurrentUser: string;
}

export interface PropertyState {
  address?: AddressTypes;
  contacts?: ContactTypes | PhoneTypes;
  emails?: EmailTypes;
  images?: ImagesTypes;
  propertyID?: string;
  propertyName?: string;
  license?: LicenseTypes;
  taxInfo?: TaxInfoTypes;
  userData?: UserDataTypes;
  websiteURL?: string;
  selfRegistration?: boolean;
  additionalData?: additionalDataTypes;
}

export interface SelectedPropertyTypes {
  label: string;
  value: string;
}

export interface ActiveProperties {
  link: string;
  isActive: boolean;
  disableOnAllProperty?: boolean;
}

export interface SelfRegistrationPropertiesType {
  propertyID: string;
  propertyName: string;
  selfRegistration: boolean;
}

export interface PropertiesState {
  selectedProperty: SelectedPropertyTypes & { activeModule: string[] };
  properties: Array<PropertyState>;
  sortedProperties: Array<SelectedPropertyTypes & { activeModule: string[] }>;
  activeProperties: Array<ActiveProperties>;
  selfRegistrationProperties: Array<SelfRegistrationPropertiesType>;
  isStraightToModule: boolean;
}

const initialState: PropertiesState = {
  selectedProperty: { label: '', value: '', activeModule: [] },
  properties: [],
  sortedProperties: [],
  activeProperties: [],
  selfRegistrationProperties: [],
  isStraightToModule: false,
};

export const PropertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    setPropertiesState: (state, action) => {
      state.properties = [...action.payload];
    },
    setSortedProperties: (state, action) => {
      state.sortedProperties = action.payload;
    },
    setActiveProperties: (state, action) => {
      state.activeProperties = action.payload;
    },
    setSelfRegistrationProperties: (state, action) => {
      state.selfRegistrationProperties = action.payload;
    },
    clearPropertiesState: (state) => {
      state.properties = [];
      state.sortedProperties = [];
      state.activeProperties = [];
      state.selfRegistrationProperties = [];
      state.selectedProperty = { label: '', value: '', activeModule: [] };
      state.isStraightToModule = false;
    },
    setSelectedProperty: (state, action) => {
      if (
        JSON.stringify(state.selectedProperty) ===
        JSON.stringify(action.payload)
      ) {
        return;
      }
      state.selectedProperty = action.payload;
    },
    setIsStraightToModule: (state, action: PayloadAction<boolean>) => {
      state.isStraightToModule = action.payload;
    },
  },
});

export default PropertiesSlice.reducer;

export const {
  setPropertiesState,
  clearPropertiesState,
  setSelectedProperty,
  setSortedProperties,
  setActiveProperties,
  setSelfRegistrationProperties,
  setIsStraightToModule,
} = PropertiesSlice.actions;
